import { createUuid } from '@notifi/uuid/browser';
import { addDays } from 'date-fns';
import { DateTime } from 'luxon';

import { dateKeyInTimeZone, dateStringToDate, dateToDateString } from '@/lib/date-values';
import { DEFAULT_BUSINESS_TIMEZONE } from '@/lib/constants/timezones';
import type { Job } from '@/types/job';
import type {
  ArrivalWindowAlignment,
  AppointmentCrewRole,
  ConfirmationMethod,
  ConfirmationStatus,
  CreateJobAppointmentDto,
  AppointmentServiceAddress,
  JobAppointment,
  PreferredTimeWindow,
  Urgency,
} from '@/types/jobAppointment';
import type { SchedulerTechnician, ServiceType } from '@/types/scheduler';
import type { AppointmentWizardSlot } from '@/components/organisms/jobs/AppointmentSlotWizard';

export interface AppointmentDetailsFieldsValue {
  issueSummary: string;
  notes: string;
  arrivalWindowEnabled: boolean;
  arrivalWindowMinutes: string;
  arrivalWindowAlignment: ArrivalWindowAlignment;
  /**
   * BR-77 — `null` means NOT CONFIGURED, and it stays null through the form.
   * Coercing an unset method to 'SMS' here made the visit-details dialog echo
   * `{ method: 'SMS' }` back on any later save — inventing a configuration
   * nobody chose, and silently undoing the repair that cleared the machine
   * writers' values. The picker shows "Not set" for it; only an operator
   * actually picking a method makes it concrete.
   */
  confirmationMethod: ConfirmationMethod | null;
  confirmationStatus: ConfirmationStatus;
  customerNotes: string;
  outcome: string;
  outcomeNotes: string;
}

export interface AppointmentEditForm extends AppointmentDetailsFieldsValue {
  timezone: string;
  technicianId: string;
  dateFrom: string;
  dateTo: string;
  preferredTimeWindow: PreferredTimeWindow;
  urgency: Urgency;
}

export interface AppointmentAvailabilitySlot {
  slotId: string;
  startAt: string;
  endAt: string;
  timezone: string;
  technicianId: string;
  technicianName: string;
  crewTechnicianIds?: string[];
  crewTechnicianNames?: string[];
  score?: number;
  assignmentRank?: number;
}

export interface AppointmentAvailabilityFilters {
  address: string;
  zipCode: string;
  dateFrom: string;
  dateTo: string;
  preferredTimeWindow: PreferredTimeWindow;
  preferredStartTime?: string;
  urgency: Urgency;
  /** Enables tenant-validated on-duty capacity for emergency Job Types. */
  jobTypeId?: string;
  serviceTypes?: string | string[];
  technicianIds?: string | string[];
  durationMinutes?: string | number;
  /** JSON-encoded full required-skill set. Legacy `minLevel` values are ignored. */
  requiredSkills?: string;
}

export interface AppointmentAddressFields {
  street: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

export const DEFAULT_APPOINTMENT_DETAILS: AppointmentDetailsFieldsValue = {
  issueSummary: '',
  notes: '',
  arrivalWindowEnabled: false,
  arrivalWindowMinutes: '60',
  arrivalWindowAlignment: 'AFTER_START',
  confirmationMethod: 'SMS',
  confirmationStatus: 'NOT_REQUESTED',
  customerNotes: '',
  outcome: '',
  outcomeNotes: '',
};

export const EMPTY_APPOINTMENT_ADDRESS: AppointmentAddressFields = {
  street: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  country: 'US',
};

export const APPOINTMENT_DATE_PRESETS: {
  label: string;
  range: (timeZone?: string | null, now?: Date) => { from: string; to: string };
}[] = [
  {
    label: 'Today',
    range: (timeZone, now) => defaultAppointmentDateRange(0, timeZone, now),
  },
  {
    label: 'Next 7 days',
    range: (timeZone, now) => defaultAppointmentDateRange(6, timeZone, now),
  },
  {
    label: 'Next 14 days',
    range: (timeZone, now) => defaultAppointmentDateRange(13, timeZone, now),
  },
  {
    label: 'Next 30 days',
    range: (timeZone, now) => defaultAppointmentDateRange(29, timeZone, now),
  },
];

export function defaultAppointmentDateRange(days = 7, timeZone?: string | null, now = new Date()) {
  const from =
    dateKeyInTimeZone(now.toISOString(), timeZone || DEFAULT_BUSINESS_TIMEZONE) ||
    dateKeyInTimeZone(now.toISOString(), DEFAULT_BUSINESS_TIMEZONE);
  const today = dateStringToDate(from) ?? now;
  return {
    from,
    to: dateToDateString(addDays(today, days)),
  };
}

export function appointmentRangeFromLocalTime(input: {
  date: string;
  time: string;
  durationMinutes: number;
  timeZone: string;
}): { startAt: string; endAt: string; timezone: string } | null {
  if (!input.date || !input.time || input.durationMinutes <= 0 || !input.timeZone) return null;

  const start = DateTime.fromISO(`${input.date}T${input.time}`, { zone: input.timeZone });
  if (
    !start.isValid ||
    start.toFormat('yyyy-MM-dd') !== input.date ||
    start.toFormat('HH:mm') !== input.time
  ) {
    return null;
  }

  const startAt = start.toUTC().toISO();
  const endAt = start.plus({ minutes: input.durationMinutes }).toUTC().toISO();
  return startAt && endAt ? { startAt, endAt, timezone: input.timeZone } : null;
}

export interface ManualSlotInput {
  date: string;
  time: string;
  durationMinutes: number;
  timeZone: string;
  technicianId: string;
  technicianName: string;
  /** Full crew (lead + helpers) when the visit is crewed; omitted for a single tech. */
  crew?: Array<{ technicianId: string; technicianName: string }>;
}

/**
 * A staff user picked a technician and a local date/time themselves (no slot
 * search). Synthesises the same `AppointmentWizardSlot` the slot picker would
 * have produced so the downstream DTO builders work unchanged; `source:
 * 'manual'` lets a host tell it apart from a found slot. `null` when the
 * inputs don't make a valid instant yet.
 */
export function buildManualSlot(input: ManualSlotInput): AppointmentWizardSlot | null {
  if (!input.technicianId) return null;
  const range = appointmentRangeFromLocalTime(input);
  if (!range) return null;
  const crew = input.crew && input.crew.length > 0 ? input.crew : null;
  return {
    slotId: createUuid(),
    source: 'manual',
    ...range,
    technicianId: input.technicianId,
    technicianName: input.technicianName,
    ...(crew
      ? {
          crewTechnicianIds: crew.map((member) => member.technicianId),
          crewTechnicianNames: crew.map((member) => member.technicianName),
        }
      : {}),
  };
}

export function addressFromJob(job?: Pick<Job, 'serviceAddress'> | null) {
  return formatAppointmentAddress(addressFieldsFromServiceAddress(job?.serviceAddress ?? null));
}

export function addressFieldsFromServiceAddress(
  address?: Partial<AppointmentServiceAddress> | null,
): AppointmentAddressFields {
  return {
    street: address?.street ?? '',
    street2: address?.street2 ?? '',
    city: address?.city ?? '',
    state: address?.state ?? '',
    zip: address?.zip ?? '',
    country: address?.country ?? 'US',
  };
}

export function addressFromServiceAddress(address?: Partial<AppointmentServiceAddress> | null) {
  return formatAppointmentAddress(addressFieldsFromServiceAddress(address));
}

export function formatAppointmentAddress(address: Partial<AppointmentAddressFields>) {
  return [address.street, address.city, address.state].filter(Boolean).join(', ');
}

export function appointmentAddressToDto(
  address: AppointmentAddressFields,
): AppointmentServiceAddress | undefined {
  const hasAddress = address.street || address.city || address.state || address.zip;
  if (!hasAddress) return undefined;
  return {
    street: address.street || undefined,
    street2: address.street2 || undefined,
    city: address.city || undefined,
    state: address.state || undefined,
    zip: address.zip || undefined,
    country: address.country || undefined,
  };
}

export function emptyToNull(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function dateOnly(value?: string | null, timeZone?: string | null) {
  if (!value) return defaultAppointmentDateRange(0, timeZone).from;
  return dateKeyInTimeZone(value, timeZone) || defaultAppointmentDateRange(0, timeZone).from;
}

export function hasValidAppointmentDateRange(dateFrom: string, dateTo: string) {
  return Boolean(dateFrom && dateTo && dateFrom <= dateTo);
}

export function availabilityDateRange(dateFrom: string, dateTo: string) {
  // Both individual and crew availability parse date-only ends inclusively.
  return { dateFrom, dateTo };
}

export function normalizeServiceTypes(raw: unknown): ServiceType[] {
  if (Array.isArray(raw)) return raw as ServiceType[];
  const maybe = raw as { data?: unknown };
  return Array.isArray(maybe?.data) ? (maybe.data as ServiceType[]) : [];
}

export function normalizeTechnicians(raw: unknown): SchedulerTechnician[] {
  if (Array.isArray(raw)) return raw as SchedulerTechnician[];
  const maybe = raw as { data?: unknown };
  return Array.isArray(maybe?.data) ? (maybe.data as SchedulerTechnician[]) : [];
}

export function getActiveServiceTypes(raw: unknown) {
  return normalizeServiceTypes(raw).filter((service) => service.isActive);
}

export function getActiveTechnicians(raw: unknown) {
  return normalizeTechnicians(raw).filter((technician) => technician.isActive);
}

// Groups skills by their Trade (the `category` field carries the trade name for
// display). Falls back to "Other" for skills without a trade.
export function groupServiceTypesByCategory(serviceTypes: ServiceType[]) {
  const grouped = new Map<string, ServiceType[]>();
  for (const service of serviceTypes) {
    const category = service.trade?.name ?? 'Other';
    if (!grouped.has(category)) grouped.set(category, []);
    grouped.get(category)!.push(service);
  }
  return Array.from(grouped.entries()).map(([category, items]) => ({ category, items }));
}

export function sortAppointmentSlots<TSlot extends { score?: number; assignmentRank?: number }>(
  slots: TSlot[],
) {
  return [...slots].sort((a, b) => {
    const aRank = a.assignmentRank;
    const bRank = b.assignmentRank;
    if (aRank != null || bRank != null) {
      if (aRank == null) return 1;
      if (bRank == null) return -1;
      if (aRank !== bRank) return aRank - bRank;
    }
    return (b.score ?? 0) - (a.score ?? 0);
  });
}

function normalizeLocalTime(value: string): string | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return null;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function appointmentSlotMatchesRecurringWindow(
  slot: Pick<AppointmentAvailabilitySlot, 'startAt' | 'endAt' | 'timezone'>,
  window: { date: string; startTime: string; durationMinutes: number },
): boolean {
  const expectedStartTime = normalizeLocalTime(window.startTime);
  if (!expectedStartTime || window.durationMinutes <= 0) return false;

  const start = DateTime.fromISO(slot.startAt, { setZone: true }).setZone(slot.timezone);
  const end = DateTime.fromISO(slot.endAt, { setZone: true }).setZone(slot.timezone);
  if (!start.isValid || !end.isValid) return false;

  return (
    start.toISODate() === window.date &&
    start.toFormat('HH:mm') === expectedStartTime &&
    Math.round(end.diff(start, 'minutes').minutes) === window.durationMinutes
  );
}

export function buildAvailabilityParams(filters: AppointmentAvailabilityFilters) {
  const range = availabilityDateRange(filters.dateFrom, filters.dateTo);
  const params: Record<string, string | number> = {
    address: filters.address.trim() || filters.zipCode.trim(),
    zipCode: filters.zipCode.trim(),
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    preferredTimeWindow: filters.preferredTimeWindow,
    urgency: filters.urgency,
  };

  const serviceTypes = joinParam(filters.serviceTypes);
  const technicianIds = joinParam(filters.technicianIds);
  if (serviceTypes) params.serviceTypes = serviceTypes;
  if (technicianIds) params.technicianIds = technicianIds;
  if (filters.requiredSkills) params.requiredSkills = filters.requiredSkills;
  if (filters.jobTypeId) params.jobTypeId = filters.jobTypeId;
  if (filters.preferredStartTime) params.preferredStartTime = filters.preferredStartTime;

  const duration = parseOptionalNumber(filters.durationMinutes);
  if (duration !== null) params.durationMinutes = duration;

  return params;
}

export function buildCrewAvailabilityParams(filters: {
  earliestStartAt?: string;
  latestFinishAt?: string;
  address: string;
  zipCode: string;
  dateFrom: string;
  dateTo: string;
  serviceType: string;
  technicianIds: string[];
  /** JT#2 — the skill/area-gated crew lead (defaults server-side to the first). */
  leadTechnicianId?: string;
  /** JT#2 — JSON-encoded required-skill set applied to the lead only. */
  requiredSkills?: string;
  preferredTimeWindow?: string;
  urgency?: string;
  /** Enables tenant-validated on-duty capacity for emergency Job Types. */
  jobTypeId?: string;
  jobId?: string;
  durationMinutes?: string | number;
}) {
  const range = availabilityDateRange(filters.dateFrom, filters.dateTo);
  const params: Record<string, string | number> = {
    address: filters.address.trim() || filters.zipCode.trim(),
    zipCode: filters.zipCode.trim(),
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
    technicianIds: filters.technicianIds.join(','),
  };
  if (filters.serviceType) params.serviceType = filters.serviceType;
  if (filters.earliestStartAt) params.earliestStartAt = filters.earliestStartAt;
  if (filters.latestFinishAt) params.latestFinishAt = filters.latestFinishAt;
  if (filters.leadTechnicianId) params.leadTechnicianId = filters.leadTechnicianId;
  if (filters.requiredSkills) params.requiredSkills = filters.requiredSkills;
  if (filters.preferredTimeWindow) params.preferredTimeWindow = filters.preferredTimeWindow;
  if (filters.urgency) params.urgency = filters.urgency;
  if (filters.jobTypeId) params.jobTypeId = filters.jobTypeId;
  if (filters.jobId) params.jobId = filters.jobId;
  const duration = parseOptionalNumber(filters.durationMinutes);
  if (duration !== null) params.durationMinutes = duration;
  return params;
}

export function appointmentToForm(appointment: JobAppointment): AppointmentEditForm {
  const arrivalWindow = appointment.arrivalWindow;
  return {
    timezone: appointment.timezone || DEFAULT_BUSINESS_TIMEZONE,
    technicianId: appointment.technicians[0]?.id ?? appointment.primaryTechnician?.id ?? 'all',
    dateFrom: dateOnly(appointment.startAt, appointment.timezone),
    dateTo: dateOnly(appointment.endAt, appointment.timezone),
    preferredTimeWindow: 'any',
    urgency: 'standard',
    issueSummary: appointment.issueSummary ?? '',
    notes: appointment.notes ?? '',
    arrivalWindowEnabled: arrivalWindow?.enabled ?? false,
    arrivalWindowMinutes: String(arrivalWindow?.minutes ?? 60),
    arrivalWindowAlignment: arrivalWindow?.alignment ?? 'AFTER_START',
    confirmationMethod: appointment.confirmationMethod ?? null,
    confirmationStatus: appointment.confirmationStatus ?? 'NOT_REQUESTED',
    customerNotes: appointment.customerNotes ?? '',
    outcome: appointment.outcome ?? '',
    outcomeNotes: appointment.outcomeNotes ?? '',
  };
}

export function buildCreateAppointmentDto({
  serviceType,
  slot,
  preferredTechnicianId,
  crew,
  details,
  serviceAddress,
  serviceAddressId,
  overrideConflicts,
}: {
  serviceType: string;
  slot: AppointmentAvailabilitySlot;
  preferredTechnicianId?: string;
  crew?: Array<{ technicianId: string; role: AppointmentCrewRole }>;
  details: AppointmentDetailsFieldsValue;
  serviceAddress?: AppointmentAddressFields;
  serviceAddressId?: string;
  /** The user acknowledged the preflight conflicts — the server skips its 409. */
  overrideConflicts?: boolean;
}): CreateJobAppointmentDto {
  const baseTechnicianIds = Array.from(
    new Set([
      slot.technicianId,
      ...(preferredTechnicianId && preferredTechnicianId !== 'all' ? [preferredTechnicianId] : []),
    ]),
  );
  const leadId = crew?.find((member) => member.role === 'LEAD')?.technicianId;
  const crewTechnicianIds =
    crew && crew.length > 0 ? crew.map((member) => member.technicianId) : null;

  return {
    scheduleMode: 'scheduled',
    serviceType,
    startAt: slot.startAt,
    endAt: slot.endAt,
    timezone: slot.timezone,
    technicianId: leadId ?? slot.technicianId,
    technicianIds: crewTechnicianIds ?? baseTechnicianIds,
    ...(crew && crew.length > 1 ? { crew } : {}),
    issueSummary: details.issueSummary.trim() || undefined,
    notes: details.notes.trim() || undefined,
    serviceAddress: serviceAddress ? appointmentAddressToDto(serviceAddress) : undefined,
    serviceAddressId,
    arrivalWindow: {
      enabled: details.arrivalWindowEnabled,
      minutes: Number(details.arrivalWindowMinutes || 0),
      alignment: details.arrivalWindowAlignment,
    },
    confirmationMethod: details.confirmationMethod ?? undefined,
    confirmationStatus: details.confirmationStatus,
    customerNotes: emptyToNull(details.customerNotes),
    outcome: emptyToNull(details.outcome),
    outcomeNotes: emptyToNull(details.outcomeNotes),
    ...(overrideConflicts ? { overrideConflicts: true } : {}),
  };
}

/** Create a visit on an existing job without assigning a time or technician.
 * The backend stores it as RESERVED so dispatch can pick it up from Schedule
 * Review later. Scheduling-only fields (crew, slot and arrival window) are
 * deliberately omitted. */
export function buildCreateUnscheduledAppointmentDto({
  serviceType,
  details,
  serviceAddress,
  serviceAddressId,
}: {
  serviceType: string;
  details: AppointmentDetailsFieldsValue;
  serviceAddress?: AppointmentAddressFields;
  serviceAddressId?: string;
}): CreateJobAppointmentDto {
  return {
    scheduleMode: 'unscheduled',
    serviceType,
    issueSummary: details.issueSummary.trim() || undefined,
    notes: details.notes.trim() || undefined,
    serviceAddress: serviceAddress ? appointmentAddressToDto(serviceAddress) : undefined,
    serviceAddressId,
    confirmationMethod: details.confirmationMethod ?? undefined,
    confirmationStatus: details.confirmationStatus,
    customerNotes: emptyToNull(details.customerNotes),
    outcome: emptyToNull(details.outcome),
    outcomeNotes: emptyToNull(details.outcomeNotes),
  };
}

/** Scheduling v2 — DAY_FLEXIBLE create (product "All day"). The visit binds to
 * a date + technician, consumes its duration from day capacity, and receives
 * concrete start/end times later from route sequencing. */
export function buildCreateDayFlexibleAppointmentDto({
  serviceType,
  scheduledDate,
  technicianId,
  durationMinutes,
  details,
  serviceAddress,
  serviceAddressId,
  timezone,
}: {
  serviceType: string;
  scheduledDate: string;
  technicianId: string;
  durationMinutes?: number;
  details: AppointmentDetailsFieldsValue;
  serviceAddress?: AppointmentAddressFields;
  serviceAddressId?: string;
  timezone?: string;
}): CreateJobAppointmentDto {
  return {
    scheduleMode: 'scheduled',
    serviceType,
    granularity: 'DAY_FLEXIBLE',
    scheduledDate,
    ...(durationMinutes ? { durationMinutes } : {}),
    ...(timezone ? { timezone } : {}),
    technicianId,
    technicianIds: [technicianId],
    issueSummary: details.issueSummary.trim() || undefined,
    notes: details.notes.trim() || undefined,
    serviceAddress: serviceAddress ? appointmentAddressToDto(serviceAddress) : undefined,
    serviceAddressId,
    confirmationMethod: details.confirmationMethod ?? undefined,
    confirmationStatus: details.confirmationStatus,
    customerNotes: emptyToNull(details.customerNotes),
    outcome: emptyToNull(details.outcome),
    outcomeNotes: emptyToNull(details.outcomeNotes),
  };
}

/**
 * Scheduling v2 — FULL_DAY / multi-day series create (backend ALL_DAY).
 * No startAt/endAt: the backend derives times from the technician's shift on
 * `scheduledDate` (NO_SHIFT_ON_DATE hard-fails, no override). `consecutiveDays`
 * (2–14) creates one appointment per day, same technician, atomically. Arrival
 * windows don't apply — the visit consumes the whole shift.
 */
export function buildCreateAllDayAppointmentDto({
  serviceType,
  scheduledDate,
  technicianId,
  crew,
  crewId,
  consecutiveDays,
  details,
  serviceAddress,
  serviceAddressId,
  timezone,
  constraints,
  urgency,
  customerRequiredTechnicianId,
  customerPreferredTechnicianId,
}: {
  constraints?: CreateJobAppointmentDto['constraints'];
  urgency?: CreateJobAppointmentDto['urgency'];
  customerRequiredTechnicianId?: string;
  customerPreferredTechnicianId?: string;
  serviceType: string;
  scheduledDate: string;
  technicianId: string;
  crew?: Array<{ technicianId: string; role: AppointmentCrewRole }>;
  crewId?: string;
  consecutiveDays?: number;
  details: AppointmentDetailsFieldsValue;
  serviceAddress?: AppointmentAddressFields;
  serviceAddressId?: string;
  /** Tenant timezone from POST /availability/days — the backend persists
   *  dto.timezone (falling back to ITS default), so omitting this stores the
   *  wrong zone for non-default tenants. */
  timezone?: string;
}): CreateJobAppointmentDto {
  return {
    scheduleMode: 'scheduled',
    constraints,
    urgency,
    customerRequiredTechnicianId,
    customerPreferredTechnicianId,
    serviceType,
    granularity: 'ALL_DAY',
    scheduledDate,
    ...(timezone ? { timezone } : {}),
    ...(consecutiveDays && consecutiveDays >= 2 ? { consecutiveDays } : {}),
    technicianId,
    technicianIds: crew?.length ? crew.map((member) => member.technicianId) : [technicianId],
    ...(crew?.length ? { crew } : {}),
    ...(crewId ? { crewId } : {}),
    issueSummary: details.issueSummary.trim() || undefined,
    notes: details.notes.trim() || undefined,
    serviceAddress: serviceAddress ? appointmentAddressToDto(serviceAddress) : undefined,
    serviceAddressId,
    confirmationMethod: details.confirmationMethod ?? undefined,
    confirmationStatus: details.confirmationStatus,
    customerNotes: emptyToNull(details.customerNotes),
    outcome: emptyToNull(details.outcome),
    outcomeNotes: emptyToNull(details.outcomeNotes),
  };
}

function joinParam(value?: string | string[]) {
  if (Array.isArray(value)) return value.filter(Boolean).join(',');
  return value?.trim() ?? '';
}

function parseOptionalNumber(value?: string | number) {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}
